import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { LomavisShortenedNumber } from '@/lomavisSharedUi/atoms/utils/numbers/LomavisShortenedNumber';
import React, { useEffect, useRef, useState } from 'react';
import './index.css';

import { Table } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import LomavisMoreInfoIcon from '@/lomavisSharedUi/atoms/icons/LomavisMoreInfoIcon/LomavisMoreInfoIcon';
import { LomavisPlatformIcon } from '@/lomavisSharedUi/atoms/icons/LomavisPlatformIcon';

// import { translateComponent } from '@/utils/tolgee/translateComponent';

export interface ComparativeColumn {
  /** The label/title for this column */
  title: string;
  /** The image URL (e.g., avatar or logo) for the column entity */
  image: string;
}
export interface DataTable {
  /** Title of the entire comparative table */
  title: string;
  /** The column definitions, usually one for each compared entity */
  columns: ComparativeColumn[];
  /** Sections grouping related rows together */
  sections: ComparativeSection[];
}

interface LomavisComparisionTableCard1Props {
  /** The text to display inside the H1 heading */
  title: string;
  data: DataTable;
  showMoreInfo?: boolean;
  enablePreview?: boolean;
}

export interface ComparativeSection {
  /** The title of the section (e.g., "Community", "Content") */
  title: string;
  /** Rows of comparative data inside this section */
  rows: ComparativeRow[];
}
export interface ComparativeRow {
  /** The description of the metric or category */
  title: string;

  moreInfo?: string;

  /** The values for each column in the same order as `columns` */
  renderAs?: string;
  values: Array<string | number>;
}

/**
 * Components: Renders a comparison table.
 * We can freeze a column by clicking the column header and unfreeze it by clicking again.
 * Freezing helps prevent horizontal scrolling.
 */
export const LomavisComparisionTableCard1: React.FC<
  LomavisComparisionTableCard1Props
> = ({ title, data, showMoreInfo = false, enablePreview = true }) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const [columns, setColumns] = useState(data.columns);
  const [sections, setSections] = useState(data.sections);
  const [frozenIndexes, setFrozenIndexes] = useState<Set<number>>(new Set()); // first column frozen
  const [stickyTopOffset, setStickyTopOffset] = useState(16);

  useEffect(() => {
    setColumns(data.columns);
    setSections(data.sections);
  }, [data]);

  useEffect(() => {
    const updateStickyTopOffset = () => {
      const appHeaderHeight =
        document.getElementById('kt_app_header')?.getBoundingClientRect()
          .height ?? 0;
      setStickyTopOffset(appHeaderHeight);
    };

    updateStickyTopOffset();
    window.addEventListener('resize', updateStickyTopOffset);

    return () => {
      window.removeEventListener('resize', updateStickyTopOffset);
    };
  }, []);

  const syncHorizontalScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const source = event.currentTarget;
    const container = source.closest('.lomavisComparisionTableCard1');
    if (!container) return;

    const header = container.querySelector<HTMLDivElement>(
      '.lomavisComparisionTableCard1HeaderScroller'
    );
    const body = container.querySelector<HTMLDivElement>(
      '.lomavisComparisionTableCard1BodyScroller'
    );

    if (!header || !body) return;

    const target = source === header ? body : header;
    if (target.scrollLeft !== source.scrollLeft) {
      target.scrollLeft = source.scrollLeft;
    }
  };

  const toggleFreezeColumn = (colIndex: number) => {
    const newFrozen = new Set(frozenIndexes);

    if (newFrozen.has(colIndex)) {
      // unfreeze
      newFrozen.delete(colIndex);
    } else {
      // freeze
      newFrozen.add(colIndex);
    }

    // reorder columns: frozen first (in their freeze order), then others
    const frozenCols = Array.from(newFrozen).map((i) => columns[i]);
    const unfrozenCols = columns.filter((_, idx) => !newFrozen.has(idx));

    const newCols = [...frozenCols, ...unfrozenCols];

    // reorder each row's values
    const newSections = sections.map((section) => ({
      ...section,
      rows: section.rows.map((row) => {
        const frozenVals = Array.from(newFrozen).map((i) => row.values[i]);
        const unfrozenVals = row.values.filter((_, idx) => !newFrozen.has(idx));
        return { ...row, values: [...frozenVals, ...unfrozenVals] };
      })
    }));

    // Update indexes to match new order
    const newFrozenIndexes = new Set(
      Array.from(newFrozen).map((i) =>
        newCols.findIndex(
          (_, idx) => idx < frozenCols.length && frozenCols[idx] === columns[i]
        )
      )
    );

    setFrozenIndexes(newFrozenIndexes);
    setColumns(newCols);
    setSections(newSections);
  };

  const handleTableDownload = async (cardRef) => {
    await handleTableScreenshot(cardRef, data?.title || 'table-export', 2);
  };

  return (
    <LomavisCard1
      title={title || data?.title}
      customDownload={handleTableDownload}
      showMoreInfo={showMoreInfo}
      enablePreview={enablePreview}
      showDownloadButton={true}
      showSheetDownloadButton={true}
      elementRef={tableRef}
    >
      <div
        className="mainWrapper lomavisComparisionTableCard1"
        ref={tableRef}
      >
        <div
          className="lomavisComparisionTableCard1HeaderSticky"
          style={{ top: stickyTopOffset }}
        >
          <div
            className="lomavisComparisionTableCard1HeaderScroller"
            onScroll={syncHorizontalScroll}
          >
            <Table
              bordered
              className="align-middle m-0 table lomavisComparisionHeaderTable"
            >
              <thead>
                <tr>
                  <th
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      width: '200px',
                      minWidth: '200px',
                      maxWidth: '200px'
                    }}
                    className="bg-white noBorder "
                  />
                  {columns.map((col, idx) => {
                    const isFrozen = idx < frozenIndexes?.size;
                    const leftOffset = isFrozen
                      ? `${(idx + 1) * 200}px`
                      : undefined;

                    return (
                      <th
                        key={idx}
                        style={{
                          position: 'sticky',
                          left: leftOffset,
                          width: '200px',
                          minWidth: '200px',
                          maxWidth: '200px',
                          zIndex: isFrozen ? 3 : 2,
                          cursor: 'pointer'
                        }}
                        className="text-center bg-light rounded border-white"
                      >
                        <div className="d-flex flex-column gap-2 align-items-center position-relative">
                          <img
                            src={col.image}
                            alt={col.title}
                            className="rounded-circle"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover'
                            }}
                          />

                          <span className="mt-1 text-gray-800 fw-bold fs-6">
                            {col.title}
                          </span>
                          <span
                            onClick={() => toggleFreezeColumn(idx)}
                            className="position-absolute top-0 end-0 px-1 cursor-pointer pinTag"
                          >
                            <i
                              className={`fa-solid fa-thumbtack fs-3 ${
                                isFrozen && 'text-primary'
                              }`}
                            ></i>
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
            </Table>
          </div>
        </div>

        <div
          className="lomavisComparisionTableCard1BodyScroller"
          onScroll={syncHorizontalScroll}
        >
          <Table
            bordered
            className="align-middle m-0 table lomavisComparisionBodyTable"
          >
            <tbody>
              {sections.map((section, sIdx) => (
                <React.Fragment key={sIdx}>
                  <tr>
                    <td
                      style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        width: '200px',
                        minWidth: '200px',
                        maxWidth: '200px'
                      }}
                      className="pt-8 p-0 border-2 border-end-0 border-start-0"
                    >
                      <span className="text-gray-500 fw-semibold fs-6 px-3 pt-3 pb-1 bg-light rounded-top mx-1">
                        {section.title}
                      </span>
                    </td>

                    {Array.from({ length: frozenIndexes?.size }).map(
                      (_, idx) => (
                        <td
                          key={`frozen-section-${idx}`}
                          style={{
                            position: 'sticky',
                            left: `${(idx + 1) * 200}px`,
                            zIndex: 2,
                            width: '200px',
                            minWidth: '200px',
                            maxWidth: '200px'
                          }}
                          className="pt-8 p-0 border-start-0 border-end-0 border-top border-bottom"
                        />
                      )
                    )}

                    {Array.from({
                      length: columns.length - frozenIndexes?.size
                    }).map((_, idx) => {
                      const isLast =
                        idx === columns.length - frozenIndexes?.size - 1;
                      return (
                        <td
                          key={`unfrozen-section-${idx}`}
                          className={`pt-8 p-0 border-start-0 border-top border-bottom ${
                            isLast ? 'border-end' : 'border-end-0'
                          }`}
                        />
                      );
                    })}
                  </tr>

                  {section.rows.map((row, rIdx) => (
                    <tr key={`${sIdx}-${rIdx}`}>
                      <td
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          width: '200px',
                          minWidth: '200px',
                          maxWidth: '200px'
                        }}
                        className="text-gray-800 bg-white fw-bold mb-1 fs-6 p-1 "
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{row.title}</span>
                          {row?.moreInfo && (
                            <span>
                              <LomavisMoreInfoIcon
                                text={row?.moreInfo}
                                size={4}
                              />
                            </span>
                          )}
                        </div>
                      </td>

                      {row.values.map((val, vIdx) => {
                        const isFrozen = vIdx < frozenIndexes?.size;
                        const leftOffset = isFrozen
                          ? `${(vIdx + 1) * 200}px`
                          : undefined;
                        return (
                          <td
                            key={vIdx}
                            style={{
                              position: isFrozen ? 'sticky' : 'relative',
                              left: leftOffset,
                              width: '200px',
                              minWidth: '200px',
                              maxWidth: '200px',
                              background: isFrozen ? '#fff' : undefined,
                              zIndex: isFrozen ? 1 : 0
                            }}
                            className="text-center fw-semibold"
                          >
                            {typeof val === 'string' &&
                            row?.renderAs === 'platform' ? (
                              val.split(', ').map((platform) => (
                                <span key={platform} className="px-1">
                                  <LomavisPlatformIcon
                                    platform={platform}
                                    size={20}
                                  />
                                </span>
                              ))
                            ) : typeof val === 'number' ? (
                              <LomavisShortenedNumber value={val} />
                            ) : (
                              val
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </LomavisCard1>
  );
};

// Add this function in your table component file
export const handleTableScreenshot = async (
  elementRef: React.RefObject<HTMLElement>,
  fileName: string = 'download',
  scale: number = 2
) => {
  const original = elementRef.current;
  if (!original) return;

  await document.fonts.ready;

  // Get the actual dimensions including scrollable content
  const { width: visibleWidth, height: visibleHeight } =
    original.getBoundingClientRect();

  // Find the scrollable wrapper to get full content dimensions
  const mainWrapper = original.querySelector('.mainWrapper');
  const table =
    original.querySelector<HTMLElement>('.lomavisComparisionBodyTable') ??
    original.querySelector<HTMLElement>('table');

  let fullWidth = visibleWidth;
  let fullHeight = visibleHeight;
  let hasHorizontalScroll = false;
  let hasVerticalScroll = false;

  if (mainWrapper && table) {
    const wrapperRect = mainWrapper.getBoundingClientRect();

    // Check if there's actual scrolling needed
    hasHorizontalScroll = table.scrollWidth > wrapperRect.width;
    hasVerticalScroll = table.scrollHeight > wrapperRect.height;

    // Only adjust dimensions if there's actual scrolling
    if (hasHorizontalScroll) {
      fullWidth = table.scrollWidth + 20; // Add some padding
    }

    if (hasVerticalScroll) {
      fullHeight = Math.max(visibleHeight, table.scrollHeight + 100); // Add padding for header

      if (hasHorizontalScroll) {
        fullHeight = fullHeight + 50; // Add some padding
      }
    }

    // Special handling for horizontal scroll tables - ensure we capture full height even if no vertical scroll
    if (hasHorizontalScroll && !hasVerticalScroll) {
      // Calculate the actual content height needed
      const tableRect = table.getBoundingClientRect();
      const wrapperTop = wrapperRect.top;
      const tableTop = tableRect.top;

      // Calculate the full height needed including the table and any content above/below it
      const contentHeight = tableTop - wrapperTop + table.scrollHeight + 50; // Add padding
      fullHeight = Math.max(visibleHeight, contentHeight);
    }
  }

  const clone = original.cloneNode(true) as HTMLElement;

  // Remove the <i> element inside the span with that specific class
  clone.querySelectorAll('span.pinTag').forEach((icon) => {
    icon.remove();
  });

  // Only apply scrolling fixes if there's actual horizontal scrolling
  if (hasHorizontalScroll || hasVerticalScroll) {
    // Handle scrollable wrappers - only if there's scrolling
    const targetDivs = clone.querySelectorAll<HTMLDivElement>('.mainWrapper');
    targetDivs.forEach((div) => {
      if (hasHorizontalScroll) {
        div.style.overflow = 'visible';
        div.style.maxWidth = 'none';
        div.style.width = 'auto';
      }
      if (hasVerticalScroll || hasHorizontalScroll) {
        // Remove height constraints when we have any kind of scrolling
        div.style.maxHeight = 'none';
        div.style.height = 'auto';
        div.style.overflowY = 'visible';
      }
    });

    // Handle tables to ensure full width is captured - only if horizontal scrolling
    if (hasHorizontalScroll) {
      const cloneTables = clone.querySelectorAll('table');
      cloneTables.forEach((table) => {
        table.style.width = 'auto';
        table.style.minWidth = 'max-content'; // Ensure table doesn't shrink
        table.style.tableLayout = 'auto'; // Allow natural table sizing
      });
    }

    // Handle sticky elements that might cause issues in screenshots - only if scrolling
    const stickyElements = clone.querySelectorAll('[style*="sticky"]');
    stickyElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.position = 'static';
      htmlElement.style.left = 'auto';
      htmlElement.style.top = 'auto';
      htmlElement.style.zIndex = 'auto';
    });

    // Set clone dimensions to accommodate full content - only if scrolling
    clone.style.width = `${fullWidth}px`;
    clone.style.height = `${fullHeight}px`;
    clone.style.minWidth = `${fullWidth}px`;
    clone.style.minHeight = `${fullHeight}px`;
  } else {
    // No scrolling detected - keep original dimensions and behavior
    clone.style.width = `${visibleWidth}px`;
    clone.style.height = `${visibleHeight}px`;
  }

  // Copy canvas content from original to clone
  const originalCanvases = original.querySelectorAll('canvas');
  const cloneCanvases = clone.querySelectorAll('canvas');

  originalCanvases.forEach((originalCanvas, index) => {
    if (index < cloneCanvases.length) {
      const cloneCanvas = cloneCanvases[index];
      const context = cloneCanvas.getContext('2d');
      if (context) {
        cloneCanvas.width = originalCanvas.width;
        cloneCanvas.height = originalCanvas.height;
        context.drawImage(originalCanvas, 0, 0);
      }
    }
  });

  // Handle font awesome icons
  clone.querySelectorAll('.fa-duotone').forEach((icon) => {
    icon.classList.remove('fa-duotone');
    icon.classList.add('fa-solid');
  });

  // Create wrapper with appropriate dimensions
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = '0';
  wrapper.style.left = '-9999px';
  wrapper.style.opacity = '1';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.width = `${fullWidth}px`;
  wrapper.style.height = `${fullHeight}px`;
  wrapper.style.minWidth = `${fullWidth}px`;
  wrapper.style.minHeight = `${fullHeight}px`;
  wrapper.style.backgroundColor = '#ffffff';
  wrapper.style.overflow =
    hasHorizontalScroll || hasVerticalScroll ? 'visible' : 'hidden';
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  try {
    // Longer delay to ensure all content is rendered
    await new Promise((resolve) => setTimeout(resolve, 500));

    const canvas = await html2canvas(clone, {
      scale,
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: false,
      backgroundColor: '#ffffff',
      width: fullWidth,
      height: fullHeight,
      scrollX: 0,
      scrollY: 0,
      windowWidth: fullWidth,
      windowHeight: fullHeight,
      ignoreElements: (element) => {
        return element.hasAttribute('data-html2canvas-ignore');
      },
      onclone: (clonedDoc, element) => {
        // Only apply additional cleanup if there's scrolling
        if (hasHorizontalScroll || hasVerticalScroll) {
          const clonedMainWrappers = element.querySelectorAll('.mainWrapper');
          clonedMainWrappers.forEach((wrapper) => {
            const htmlElement = wrapper as HTMLElement;
            if (hasHorizontalScroll) {
              htmlElement.style.overflow = 'visible';
              htmlElement.style.maxWidth = 'none';
            }
            if (hasVerticalScroll || hasHorizontalScroll) {
              // Always remove height constraints when there's any scrolling
              htmlElement.style.maxHeight = 'none';
              htmlElement.style.overflowY = 'visible';
            }
          });

          // Ensure all sticky elements are static
          const clonedStickyElements = element.querySelectorAll(
            '[style*="position: sticky"], [style*="position:sticky"]'
          );
          clonedStickyElements.forEach((elem) => {
            const htmlElem = elem as HTMLElement;
            htmlElem.style.position = 'static';
          });
        }

        return element;
      }
    });

    const dataURL = canvas.toDataURL('image/jpeg', 1.0);
    const link = document.createElement('a');
    link.href = dataURL;
    const sanitizedFileName = fileName
      .trim()
      .replace(/[^\w-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');

    link.download = `${sanitizedFileName}.jpeg`;
    link.click();
  } finally {
    document.body.removeChild(wrapper);
  }
};
